import React, { useState, useEffect } from "react";
import {
    Search, RefreshCw, AlertTriangle, CheckCircle2, X,
    Clock, Tag, FileText, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, Info, ShieldAlert,
    Cpu, Database, Terminal
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown "; // Adjust import path

interface IloIssue {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    ilo_report_id: number;
    severity: string;
    component: string;
    sub_component: string;
    description: string;
    evidence: string; // JSON string
    is_resolved: boolean;
    resolved_at: string | null;
}

interface AssetIloIssuesProps {
    assetId: string | number;
}

export default function AssetIloIssues({ assetId }: AssetIloIssuesProps) {
    const [issues, setIssues] = useState<IloIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Sidebar state
    const [selectedIssue, setSelectedIssue] = useState<IloIssue | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchIssues = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId
            };

            if (searchTerm) {
                queryObj.description = { $ilike: `%${searchTerm}%` };
            }

            const payload: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/asset_ilo_issues", { params: payload });

            if (response.data.status) {
                setIssues(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length || 0);
            } else {
                showError(response.data.message || "Failed to load issues");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchIssues();
        }
    }, [currentPage, limit, assetId]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchIssues();
    };

    const handleRowClick = (issue: IloIssue) => {
        setSelectedIssue(issue);
        setIsSidebarOpen(true);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
        setTimeout(() => setSelectedIssue(null), 300); // Clear after slide animation
    };

    const renderSeverityBadge = (severity: string) => {
        if (!severity) return <span className="text-slate-400 dark:text-gray-500">-</span>;

        const s = severity.toLowerCase();

        if (s === "critical" || s === "fatal") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-50 dark:bg-red-500/10 text-rose-700 dark:text-red-400 border border-rose-200 dark:border-red-500/20 whitespace-nowrap">
                    <AlertTriangle size={10} /> {severity}
                </div>
            );
        } else if (s === "warning") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 dark:bg-yellow-500/10 text-amber-700 dark:text-yellow-400 border border-amber-200 dark:border-yellow-500/20 whitespace-nowrap">
                    <AlertTriangle size={10} /> {severity}
                </div>
            );
        } else if (s === "ok" || s === "info" || s === "good") {
            return (
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 whitespace-nowrap">
                    <CheckCircle2 size={10} /> {severity}
                </div>
            );
        }

        return (
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 whitespace-nowrap">
                <Info size={10} /> {severity}
            </div>
        );
    };

    const parseEvidence = (evidenceStr: string) => {
        if (!evidenceStr) return null;
        try {
            return JSON.parse(evidenceStr);
        } catch (e) {
            return evidenceStr;
        }
    };

    const renderEvidenceData = (data: any) => {
        if (!data) return <p className="text-[11px] text-slate-500 dark:text-gray-200">No evidence provided.</p>;
        
        if (typeof data !== 'object') {
            return <p className="text-[11px] text-slate-800 dark:text-gray-300 font-mono break-all">{String(data)}</p>;
        }

        const renderRecursive = (obj: any): React.ReactNode => {
            return Object.entries(obj).map(([key, value]) => {
                const formattedKey = key.replace(/_/g, ' ').toUpperCase();
                
                if (typeof value === 'object' && value !== null) {
                    return (
                        <div key={key} className="col-span-full mt-1 bg-slate-100 dark:bg-gray-900/60 rounded-lg p-3 border border-slate-200 dark:border-gray-700/50">
                            <p className="text-[9px] font-bold text-slate-700 dark:text-gray-200 tracking-widest mb-3 flex items-center gap-2">
                                <Terminal size={12} className="text-slate-400 dark:text-gray-500"/> {formattedKey}
                            </p>
                            <div className="grid grid-cols-1 gap-2">
                                {renderRecursive(value)}
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={key} className="flex flex-col bg-slate-50 dark:bg-gray-800/40 p-2.5 rounded-lg border border-slate-200 dark:border-gray-700/50">
                        <span className="text-[9px] text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">{formattedKey}</span>
                        <span className="text-[11px] text-slate-800 dark:text-gray-200 font-mono break-all">{String(value)}</span>
                    </div>
                );
            });
        };

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {renderRecursive(data)}
            </div>
        );
    };

    return (
        <div className="flex flex-col text-slate-900 dark:text-gray-200 relative overflow-hidden pb-4 px-2">
            {/* Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <ShieldAlert className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Hardware Issues</h2>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <form onSubmit={handleSearch} className="relative w-64 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-200 group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search description..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-all shadow-sm"
                        />
                    </form>
                    <button
                        onClick={() => { setCurrentPage(1); fetchIssues(); }}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-700 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm cursor-pointer"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : 'group-hover:text-indigo-500'} />
                    </button>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl flex flex-col max-h-full">
                <div className="relative overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full text-left border-collapse table-fixed">
                        <thead className="bg-slate-100 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <th className="w-[10%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Severity
                                </th>
                                <th className="w-[12%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Cpu size={12} className="text-blue-600 dark:text-blue-400 shrink-0" /> <span>Component</span>
                                    </div>
                                </th>
                                <th className="w-[12%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Sub Component
                                </th>
                                <th className="w-[35%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <FileText size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" /> <span>Description</span>
                                    </div>
                                </th>
                                <th className="w-[16%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <Clock size={12} className="text-amber-500 dark:text-yellow-500 shrink-0" /> <span>Detected</span>
                                    </div>
                                </th>
                                <th className="w-[15%] px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-gray-700/60 bg-white dark:bg-gray-900/20 text-xs">
                            {loading && issues.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                                            <p className="text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest">Fetching Issues...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : issues.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-40">
                                            <CheckCircle2 size={32} className="text-emerald-600 dark:text-emerald-500 mb-2" />
                                            <p className="text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">No hardware issues detected</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                issues.map((issue) => (
                                    <tr 
                                        key={issue.id} 
                                        onClick={() => handleRowClick(issue)}
                                        className="hover:bg-slate-50 dark:hover:bg-gray-800/60 transition-colors group cursor-pointer"
                                    >
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {renderSeverityBadge(issue.severity)}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-[11px] font-medium text-slate-800 dark:text-gray-300">
                                            {issue.component || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-slate-600 dark:text-gray-200">
                                            {issue.sub_component || "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {issue.description ? (
                                                <span 
                                                    className="text-[11px] text-slate-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors" 
                                                    title={issue.description}
                                                >
                                                    {issue.description.length > 50 
                                                        ? `${issue.description.substring(0, 50)}...` 
                                                        : issue.description}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 dark:text-gray-500">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap font-mono text-[10px] text-slate-600 dark:text-gray-200">
                                            {issue.created ? new Date(issue.created + "Z").toLocaleString() : "-"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {issue.is_resolved ? (
                                                <div className="flex flex-col">
                                                    <span className="text-emerald-600 dark:text-emerald-400 text-[10px] font-bold flex items-center gap-1">
                                                        <CheckCircle2 size={10}/> Resolved
                                                    </span>
                                                    <span className="text-slate-400 dark:text-gray-500 text-[8px] font-mono mt-0.5">
                                                        {issue.resolved_at ? new Date(issue.resolved_at + "Z").toLocaleDateString() : ""}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-rose-600 dark:text-red-400 text-[10px] font-bold flex items-center gap-1">
                                                    <AlertTriangle size={10}/> Active
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0 mt-auto">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => {
                                setLimit(v);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[9px] font-medium text-slate-600 dark:text-gray-200 tracking-widest">
                            <span className="text-slate-900 dark:text-gray-300 font-semibold">
                                {totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1}
                            </span>
                            {" - "}
                            <span className="text-slate-900 dark:text-gray-300 font-semibold">
                                {Math.min(currentPage * limit, totalRecords)}
                            </span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-900 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                    </div>

                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)} className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"><ChevronsLeft size={14} /></button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"><ChevronLeft size={14} /></button>
                        <div className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-400 rounded text-[10px] font-bold mx-1">{currentPage}</div>
                        <button disabled={currentPage * limit >= totalRecords} onClick={() => setCurrentPage(prev => prev + 1)} className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"><ChevronRight size={14} /></button>
                        <button disabled={currentPage * limit >= totalRecords} onClick={() => setCurrentPage(Math.ceil(totalRecords / limit))} className="p-1 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 transition-colors bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded cursor-pointer"><ChevronsRight size={14} /></button>
                    </div>
                </div>
            </div>

            {/* Sliding Sidebar for Details */}
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity" 
                    onClick={closeSidebar}
                />
            )}

            <div
                className={`fixed top-0 right-0 h-full w-[450px] bg-white dark:bg-gray-900 border-l border-slate-200 dark:border-gray-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
                    isSidebarOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-gray-800 bg-slate-50/95 dark:bg-gray-900/95 sticky top-0 z-10">
                    <div>
                        <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                            <Database size={16} className="text-indigo-600 dark:text-indigo-400" /> Issue Details
                        </h2>
                        <div className="flex items-center gap-2 mt-2">
                            {selectedIssue && renderSeverityBadge(selectedIssue.severity)}
                            {selectedIssue?.is_resolved && (
                                <span className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 rounded text-[9px] font-bold uppercase">
                                    Resolved
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={closeSidebar}
                        className="p-2 rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Sidebar Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-6 bg-slate-50/50 dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-950">
                    {selectedIssue && (
                        <div className="flex flex-col gap-6">
                            
                            {/* Metadata Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white dark:bg-gray-800/40 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm">
                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">Component</p>
                                    <p className="text-xs font-bold text-slate-900 dark:text-gray-200">{selectedIssue.component}</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800/40 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 shadow-sm">
                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">Sub Component</p>
                                    <p className="text-xs font-bold text-slate-900 dark:text-gray-200">{selectedIssue.sub_component || "-"}</p>
                                </div>
                            </div>

                            {/* Description Section */}
                            <div>
                                <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-3 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                                    <FileText size={14} className="text-blue-600 dark:text-blue-400" /> Full Description
                                </h3>
                                <div className="bg-white dark:bg-gray-800/40 p-4 rounded-xl border border-slate-200 dark:border-gray-700/50 text-xs text-slate-700 dark:text-gray-300 leading-relaxed shadow-sm">
                                    {selectedIssue.description}
                                </div>
                            </div>

                            {/* Evidence Section (Parsed and Looped) */}
                            <div>
                                <h3 className="text-[11px] font-black text-slate-800 dark:text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-3 border-b border-slate-200 dark:border-gray-700/50 pb-2">
                                    <Tag size={14} className="text-pink-600 dark:text-pink-400" /> Evidence / Context
                                </h3>
                                <div className="mt-2">
                                    {renderEvidenceData(parseEvidence(selectedIssue.evidence))}
                                </div>
                            </div>

                            {/* Timestamps */}
                            <div className="pt-4 border-t border-slate-200 dark:border-gray-800 flex flex-col gap-2 mt-4">
                                <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-gray-400 font-mono">
                                    <span>Detected:</span>
                                    <span className="text-slate-800 dark:text-gray-400">{selectedIssue.created ? new Date(selectedIssue.created + "Z").toLocaleString() : "-"}</span>
                                </div>
                                {selectedIssue.is_resolved && selectedIssue.resolved_at && (
                                    <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-gray-400 font-mono">
                                        <span>Resolved:</span>
                                        <span className="text-emerald-600 dark:text-emerald-400/80 font-semibold">{new Date(selectedIssue.resolved_at + "Z").toLocaleString()}</span>
                                    </div>
                                )}
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}